import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { default as InlineSVG, default as SVG } from "react-inlinesvg";
import useSWR, { useSWRConfig } from "swr";
import Auth from "../../Auth";
import useInertRef from "../../hooks/useInertRef";
import usePopoverClose from "../../hooks/usePopoverClose";
import { useCommentStore } from "../../stores/commentStore";
import { useProjectStore } from "../../stores/projectStore";
import { useSudowriteStore } from "../../stores/sudowriteStore";
import { sendAppEvent } from "../../Utils.js";
import { Button } from "../Button/index.js";
import Scrollable from "../Scrollable";
import SplitButton from "../SplitButton/index.js";
import Text from "../Text";
import { useToast } from "../ToastWrapper/index.js";
import { ShareLink } from "./ShareLink.jsx";
import { ErrorMessage, Overlay, ShareLinks, StyledDocumentSharingMenu } from "./styles";

async function copyToClipboard(text) {
  if (!navigator.clipboard?.writeText) return false;

  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function useCurrentlySharedDocuments(projectId) {
  const [errorMessage, setErrorMessage] = React.useState(null);
  const {
    data: currentlySharedDocuments,
    mutate,
    error,
    isLoading: isLoadingCurrentlySharedDocuments,
  } = useSWR(`/api/projects/${projectId}/shared-documents/get`, (url) => {
    return Auth.fetchWithIdToken(url);
  }, {
    revalidateOnFocus: false,
  });
  const { cache } = useSWRConfig();

  const sharedDocuments = Array.isArray(currentlySharedDocuments) ? currentlySharedDocuments : [];

  function createUnpublishHandler(document) {
    return async function handleUnpublish() {
      try {
        // To avoid an awkward UI state until the delete happens, invalidate the currently shared documents if there's
        // only one shared document and it's about to be deleted
        if (sharedDocuments.length === 1) {
          cache.delete(`/api/projects/${projectId}/shared-documents/get`);
        }
        await Auth.fetchWithIdToken(`/api/documents/${document.shortId}/shared`, {
          method: "DELETE",
        });
        await mutate();
        setErrorMessage(null);
      } catch (e) {
        setErrorMessage(e.message);
      }
    };
  }

  function createToggleIndentHandler(document) {
    return async function handleToggleIndent(nextValue) {
      try {
        await Auth.fetchWithIdToken(`/api/documents/${document.shortId}/shared`, {
          method: "PUT",
          textIndent: nextValue,
        });
        await mutate();
        setErrorMessage(null);
      } catch (e) {
        setErrorMessage(e.message);
        throw e;
      }
    };
  }

  async function publishDocument(document) {
    try {
      const response = await Auth.fetchWithIdToken(`/api/documents/${document.id}/shared`, {
        documentId: document.id,
        textIndent: document.textIndent,
      });
      mutate();
      setErrorMessage(null);
      return response;
    } catch (e) {
      setErrorMessage(e.message);
    }
  }

  useEffect(() => {
    if (error) {
      setErrorMessage("Failed to fetch shared documents");
    }
  }, [error]);

  if (!currentlySharedDocuments) {
    return { currentlySharedDocuments: [], publishDocument, errorMessage };
  }

  const mappedDocuments = sharedDocuments.map((doc) => ({
    ...doc,
    publishedAt: new Date(doc.publishedAt),
    updatedAt: new Date(doc.updatedAt),
    url: `${window.location.origin}/app/documents/${doc.shortId}/feedback`,
    onUnpublishLink: createUnpublishHandler(doc),
    onToggleIndent: createToggleIndentHandler(doc),
  }));

  return { currentlySharedDocuments: mappedDocuments, publishDocument, errorMessage, isLoadingCurrentlySharedDocuments };
}

function DocumentSharingMenu({ documentId }) {
  const [open, setOpen] = useState(false);
  const userData = useSudowriteStore((state) => state.userData);
  const subscription = useSudowriteStore((state) => state.subscription);
  const editorOptions = useSudowriteStore((state) => state.editorOptions);

  // onboarding
  const [showCleanCopyOnboarding, setShowCleanCopyOnboarding] = React.useState(false);
  const [cleanCopyOnboardingIsExitable, setCleanCopyOnboardingIsExitable] = React.useState(true);
  const [showTooltip, setShowTooltip] = React.useState(false);
  const { showCleanCopyScreenOverlay, setShowCleanCopyScreenOverlay } = useCommentStore((state) => state);

  const { project, contents: projectDocuments } = useProjectStore();
  const hideCleanCopyDuringImport = !!(project?.importStep && project.importStep !== "CANCELLED");
  const documentSharingContainerRef = useRef(null);
  const [elementHeight, setElementHeight] = useState(`calc(100vh - 200px)`);
  const [isLoading, setIsLoading] = useState(false);
  const { setToast } = useToast();

  const [displayedDocument, setDisplayedDocument] = useState(null);
  const { currentlySharedDocuments, publishDocument, errorMessage, isLoading: isLoadingCurrentlySharedDocuments } = useCurrentlySharedDocuments(project.id);
  // Each row needs its own portal ref. Keep the ref slots stable while the count
  // is unchanged, since usePopoverClose refreshes containment when the count changes.
  const portalRefs = useMemo(() => Array.from({ length: currentlySharedDocuments.length }, () => React.createRef()), [currentlySharedDocuments.length]);
  const options = (projectDocuments || [])
    .filter((doc) => doc.id === documentId || !currentlySharedDocuments.some((sharedDoc) => sharedDoc.documentId === doc.id))
    .map((doc) => ({ label: doc.title, value: doc.id, ...doc }));

  const defaultOption = options.find((doc) => doc.id === documentId);

  useEffect(() => {
    if (!subscription.hasAccessToCleanCopy) {
      return;
    }
    // if the user has no shared documents, show the onboarding and make it not exitable
    // but if the user has shared documents and has not disabled the onboarding:
    if (currentlySharedDocuments.length === 0 || !userData.data.hideCleanCopyShareMenuOnboarding) {
      setShowCleanCopyOnboarding(true);
      if (!isLoadingCurrentlySharedDocuments && currentlySharedDocuments.length === 0) {
        setCleanCopyOnboardingIsExitable(false);
      }
    } else {
      setShowCleanCopyOnboarding(false);
      setCleanCopyOnboardingIsExitable(true);
    }
  }, [userData.data.hideCleanCopyShareMenuOnboarding, currentlySharedDocuments.length]);

  useEffect(() => {
    if (!subscription.hasAccessToCleanCopy) {
      return;
    }

    if (!userData.data.hideCleanCopyShareMenuTooltip) {
      setShowTooltip(true);
    }
  }, [userData.data.hideCleanCopyShareMenuTooltip]);

  useEffect(() => {
    if (documentSharingContainerRef.current) {
      const height = documentSharingContainerRef.current.getBoundingClientRect().height;
      setElementHeight(`calc(100vh - ${height + 150}px)`);
    }
  }, []);

  function handleSave(selectedOption) {
    const alreadyShared = currentlySharedDocuments.find((doc) => doc.documentId === selectedOption.id);
    if (!!alreadyShared) {
      copyToClipboard(alreadyShared.url).then((copied) => {
        setToast(
          <div className='flex items-center gap-2'>
            <InlineSVG
              src="/assets/check-simple.svg"
              className="success"
            />
            {copied ? "Document is already shared. Link copied to clipboard." : "Document is already shared."}
          </div>
        );
      });
      return;
    }
    setIsLoading(true);

    sendAppEvent("CleanCopy - Share Document", {
      documentId: selectedOption.id,
    });

    publishDocument({
      id: selectedOption.id,
      textIndent: editorOptions?.textIndent ?? true,
    })
      .then(
        (doc) => {
          handleCloseCleanCopyOnboarding();
          copyToClipboard(`${window.location.origin}/app/documents/${doc.shortId}/feedback`).then((copied) =>
            setToast(
              <div className='flex items-center gap-2'>
                <InlineSVG
                  src="/assets/check-simple.svg"
                  className="success"
                />
                {copied ? "Your document is now shared. Link copied to clipboard." : "Your document is now shared."}
              </div>
            )
          );
        },
        () => {
          setToast(
            <div className='flex items-center gap-2'>
              <InlineSVG
                src="/assets/close.svg"
                className="failed"
              />
              Something went wrong. Please try again.
            </div>
          );
        }
      )
      .finally(() => {
        setIsLoading(false);
      });
  }
  function handleSelect(selectedOption) {
    setDisplayedDocument(selectedOption.label);
  }

  const menuInertRef = useInertRef(open);
  const { containerRef, handleBlur } = usePopoverClose(open, () => setOpen(false), {
    refs: portalRefs,
  });

  const handleCloseCleanCopyOnboarding = () => {
    setShowCleanCopyOnboarding(false);
    userData.update({ hideCleanCopyShareMenuOnboarding: true });
  };

  const handleCloseTooltip = () => {
    setShowTooltip(false);
    userData.update({ hideCleanCopyShareMenuTooltip: true });
  };

  const handleClickOnboardingOverlay = () => {
    setShowCleanCopyScreenOverlay(false);
    setOpen(false);
  };

  if (hideCleanCopyDuringImport) {
    return null;
  }

  return (
    <StyledDocumentSharingMenu showCleanCopyOnboarding={showCleanCopyOnboarding}>
      <div
        id="document-sharing-container"
        className={open ? "document-sharing-open" : undefined}
        ref={containerRef}
        onBlur={handleBlur}
      >
        <Button
          secondaryGrey={true}
          className="use-component-styles"
          aria-label="Open Document Sharing Menu"
          iconSize={24}
          disabled={subscription.isBannedFromSharingDocuments}
          onClick={() => {
            if (showTooltip) {
              handleCloseTooltip();
            }
            setOpen(!open);
          }}
        >
          <SVG src={"/assets/export-icon.svg"} />
        </Button>

        <div ref={menuInertRef}>
          <AnimatePresence>
            {open && (
              <motion.div
                transition={{ duration: 0.15 }}
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                className="document-sharing-menu"
                ref={documentSharingContainerRef}
              >
              {showCleanCopyOnboarding ? (
                <div className="clean-copy-onboarding">
                  {cleanCopyOnboardingIsExitable ? (
                    <InlineSVG
                      src="/assets/close.svg"
                      className="close"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleCloseCleanCopyOnboarding();
                      }}
                    />
                  ) : null}
                  <header>
                    <h1>Collect feedback from your pals!</h1>
                  </header>
                  <p>Share a link and each reader gets their own clean copy of your chapter to comment on.</p>
                  <div className="onboarding-gif-container">
                    <video
                      src="/assets/clean-copy-author-onboarding.mp4"
                      autoPlay
                      muted
                      loop
                      playsInline
                    />
                  </div>
                  {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}
                  <footer>
                    <p>Get Started -{">"} </p>
                    <SplitButton
                      onClick={handleSave}
                      onSelect={handleSelect}
                      options={options}
                      defaultOption={defaultOption}
                      dropdownMaxHeight={elementHeight}
                      isLoading={isLoading}
                      duration={1}
                    >
                      <div className='text-left' style={{ lineHeight: 1.2 }}>
                        <div>Share</div>
                        {displayedDocument ? (
                          <Text
                            truncate
                            fontSize="10px"
                          >
                            {displayedDocument}
                          </Text>
                        ) : null}
                      </div>
                    </SplitButton>
                  </footer>
                </div>
              ) : (
                <>
                  <h1>Share a Document</h1>
                  <p>Anyone with the link will be able to read it.</p>
                  {currentlySharedDocuments.length ? (
                    <>
                      <h1>Shared from this Project:</h1>
                      <Scrollable>
                        <ShareLinks>
                          {currentlySharedDocuments.map((linkProps, index) => (
                            <ShareLink
                              key={linkProps.documentId}
                              projectDocuments={projectDocuments}
                              portalRef={portalRefs[index]}
                              {...linkProps}
                            />
                          ))}
                        </ShareLinks>
                      </Scrollable>
                    </>
                  ) : null}
                  {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}
                  <SplitButton
                    onClick={handleSave}
                    onSelect={handleSelect}
                    options={options}
                    defaultOption={defaultOption}
                    dropdownMaxHeight={elementHeight}
                    isLoading={isLoading}
                    duration={1}
                  >
                    <div className='text-left' style={{ lineHeight: 1.2 }}>
                      <div>Share</div>
                      {displayedDocument ? (
                        <Text
                          truncate
                          fontSize="10px"
                        >
                          {displayedDocument}
                        </Text>
                      ) : null}
                    </div>
                  </SplitButton>
                </>
              )}
            </motion.div>
          )}
          </AnimatePresence>
        </div>
        {showCleanCopyScreenOverlay && <Overlay onClick={handleClickOnboardingOverlay} />}
      </div>
    </StyledDocumentSharingMenu>
  );
}

export default DocumentSharingMenu;
